import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useIsRedoAdmin } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getFirstInstallRedirectPath,
  isWelcomeIndexPath,
} from "./first-install-redirect";
import { loadInitialFirstInstallState } from "./load-first-install-step";

export const FirstInstallOnboardingPendingContext = createContext(false);

// Replaces the old full-screen onboarding modal: when first-install onboarding
// is incomplete, redirect to /welcome, which owns the whole flow.
export const FirstInstallRedirectGuard = memo(
  function FirstInstallRedirectGuard({ children }: { children: ReactNode }) {
    const team = useContext(TeamContext);
    const client = useRequiredContext(RedoMerchantClientContext);
    const isRedoAdmin = useIsRedoAdmin();
    const navigate = useNavigate();
    const location = useLocation();
    const locationRef = useRef(location.pathname);
    locationRef.current = location.pathname;
    const redirectedRef = useRef(false);

    const requiredLoad = useLoad(async (signal) => {
      if (!team || isRedoAdmin || isWelcomeIndexPath(locationRef.current)) {
        return false;
      }
      const { required } = await loadInitialFirstInstallState(client, signal);
      return required;
    }, []);

    useEffect(() => {
      if (redirectedRef.current || !team || requiredLoad.pending) {
        return;
      }
      const path = getFirstInstallRedirectPath({
        required: requiredLoad.value ?? false,
        isRedoAdmin,
        pathname: locationRef.current,
        teamId: String(team._id),
      });
      if (path) {
        redirectedRef.current = true;
        navigate(path, { replace: true });
      }
    }, [isRedoAdmin, navigate, requiredLoad.pending, requiredLoad.value, team]);

    const firstInstallOnboardingPending =
      requiredLoad.pending || requiredLoad.value === true;

    return (
      <FirstInstallOnboardingPendingContext.Provider
        value={firstInstallOnboardingPending}
      >
        {children}
      </FirstInstallOnboardingPendingContext.Provider>
    );
  },
);
